import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const DEFAULT_MAX_ACTIVE_PLAYERS = 6;

// FR-007: a player runs only while its tile is on screen, and never more than `max` at
// once — the first visible tiles in mosaic order win.
const active = (orderedIds: string[], visible: ReadonlySet<string>, max: number): string[] =>
    orderedIds.filter((id) => visible.has(id)).slice(0, max);

export const tileSelection = { active };

type ObserverFactory = (callback: IntersectionObserverCallback) => IntersectionObserver | null;

const browserObserver: ObserverFactory = (callback) =>
    typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(callback, { threshold: 0.25 });

const TILE_ID_ATTRIBUTE = "data-segment-id";

// Without an observer nothing counts as visible, so no player starts: the mosaic
// degrades to static tiles instead of running every player at once.
export const useVisibleTiles = (
    orderedIds: string[],
    max: number,
    createObserver: ObserverFactory = browserObserver,
): { register: (segmentId: string) => (element: HTMLElement | null) => void; activeIds: ReadonlySet<string> } => {
    const [visible, setVisible] = useState<ReadonlySet<string>>(() => new Set());
    const observerRef = useRef<IntersectionObserver | null>(null);
    const elements = useRef(new Map<string, HTMLElement>());

    useEffect(() => {
        const observer = createObserver((entries) => {
            setVisible((current) => {
                const next = new Set(current);
                for (const entry of entries) {
                    const id = entry.target.getAttribute(TILE_ID_ATTRIBUTE);
                    if (id === null) continue;
                    if (entry.isIntersecting) next.add(id);
                    else next.delete(id);
                }
                return next;
            });
        });
        observerRef.current = observer;
        if (observer !== null) elements.current.forEach((element) => observer.observe(element));
        return () => {
            observer?.disconnect();
            observerRef.current = null;
        };
    }, [createObserver]);

    const callbacks = useRef(new Map<string, (element: HTMLElement | null) => void>());

    const attach = useCallback((segmentId: string, element: HTMLElement | null): void => {
        const previous = elements.current.get(segmentId);
        if (previous !== undefined && previous !== element) {
            observerRef.current?.unobserve(previous);
            elements.current.delete(segmentId);
            setVisible((current) => {
                if (current.has(segmentId) === false) return current;
                const next = new Set(current);
                next.delete(segmentId);
                return next;
            });
        }
        if (element === null) return;
        element.setAttribute(TILE_ID_ATTRIBUTE, segmentId);
        elements.current.set(segmentId, element);
        observerRef.current?.observe(element);
    }, []);

    // One stable ref callback per tile, so a re-render never detaches and re-attaches it.
    const register = useCallback((segmentId: string) => {
        const cached = callbacks.current.get(segmentId);
        if (cached !== undefined) return cached;
        const callback = (element: HTMLElement | null): void => attach(segmentId, element);
        callbacks.current.set(segmentId, callback);
        return callback;
    }, [attach]);

    const activeIds = useMemo(() => new Set(active(orderedIds, visible, max)), [orderedIds, visible, max]);
    return { register, activeIds };
};
